import { TransactionImportStatus } from '@/components/cashflow/TransactionImportStatus';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { AlertCircle } from '@/components/common/icons';
import Footer from '@/components/compliance/Footer';
import { useIsMasquerading } from '@/services/stytch';
import { Box, BoxProps, Divider, FlexProps } from '@chakra-ui/react';
import { useCurrentUser } from '@common/react/user';
import { FC } from 'react';
import ErrorBoundary from '../ErrorBoundary';
import PlentyFullScreenSpinner from '../PlentyFullScreenSpinner';
import LeftNavSidebar, { SIDEBAR_WIDTH } from './LeftNavSidebar';
import { NavProps } from './Nav';
import {
    SHUTDOWN_BANNER_HEIGHT,
    ShutdownBanner,
    useIsShutdownBannerVisible,
} from './ShutdownBanner';

export interface LeftNavPageLayout extends BoxProps, NavProps {
    showPartnerSwitcher?: boolean;
    showUserMenu?: boolean;
    showFooter?: boolean;
    showInteractiveToolDisclosure?: boolean;
    centerProps?: FlexProps;
    header?: React.ReactNode;
}

const LeftNavPageLayout: FC<LeftNavPageLayout> = ({
    children,
    isAppLoading,
    showFooter = true,
    showInteractiveToolDisclosure,
    centerProps = {},
    header,
    ...props
}) => {
    const { userInfo } = useCurrentUser();
    const isMasquerading = useIsMasquerading();
    const isShutdownBannerVisible = useIsShutdownBannerVisible();

    return (
        <>
            {isShutdownBannerVisible && <ShutdownBanner />}
            {isMasquerading && (
                <FlexRow
                    bg="black"
                    h={9}
                    top={0}
                    left={0}
                    right={0}
                    mb={100}
                    position="fixed"
                    color="white"
                    align="center"
                    justify="center"
                >
                    <AlertCircle mr={2} color="red.500" />
                    Masquerading as {userInfo.firstName} {userInfo.lastName}
                </FlexRow>
            )}
            <FlexRow
                w="full"
                {...props}
                mt={
                    isMasquerading
                        ? 9
                        : isShutdownBannerVisible
                          ? SHUTDOWN_BANNER_HEIGHT
                          : 0
                }
            >
                <TransactionImportStatus />
                <LeftNavSidebar />
                <ErrorBoundary>
                    <FlexRow w="full" ml={SIDEBAR_WIDTH}>
                        <FlexRow basis={0} grow={1} />
                        {/* Center */}
                        <FlexCol
                            basis={0}
                            grow={20}
                            minW={300}
                            maxW={1400}
                            {...centerProps}
                        >
                            {isAppLoading ? (
                                <PlentyFullScreenSpinner />
                            ) : (
                                <Box position="relative">
                                    <FlexCol w="full" grow={1} minH="100vh">
                                        <FlexRow
                                            h={18}
                                            align="center"
                                            justify="flex-end"
                                            gap={4}
                                        >
                                            {header}
                                        </FlexRow>

                                        {children}
                                    </FlexCol>
                                    {showFooter && (
                                        <>
                                            <Divider />
                                            <Footer
                                                showInteractiveToolDisclosure={
                                                    showInteractiveToolDisclosure
                                                }
                                                bg="white"
                                            />
                                        </>
                                    )}
                                </Box>
                            )}
                        </FlexCol>
                        <FlexRow basis={0} grow={2} />
                    </FlexRow>
                </ErrorBoundary>
            </FlexRow>
        </>
    );
};

export default LeftNavPageLayout;
